import React from "react";

import IconBase, { IconBaseProps } from "./IconBase";

/**
 * Blood Count — 20×20 lab group icon (Figma 834:53).
 *
 * The Figma symbol composes four separate vector layers, each with its
 * own native viewBox sized to the layer's allocated rectangle inside the
 * 20×20 parent. Here we host all four paths in a single 20×20 viewBox
 * and translate each to its inset offset; no scaling is needed because
 * the native viewBox of each vector already matches its allocated area.
 *
 * Layer offsets (from the Figma inset percentages on a 20×20 parent):
 *   • Vector  834:27 — translate(4.584, 4.648)   (6.667 × 3.269)
 *   • Vector  834:28 — translate(2.294, 2.500)   (11.039 × 12.852)
 *   • Vector  834:29 — translate(13.452, 7.524)  (3.687 × 2.879)
 *   • Vector  834:30 — translate(9.478, 10.908)  (8.206 × 6.408)
 *
 * Paths use `fill` so the icon picks up the `color` prop (IconBase sets
 * `fill={color}` on the `<svg>` element, which child paths inherit).
 */
const IconBloodCount: React.FC<IconBaseProps> = ({
  size = 24,
  color = "currentColor",
  className = "",
  ...props
}) => {
  const iconSize =
    typeof size === "number" ? size : parseInt(size as string, 10) || 24;
  return (
    <IconBase
      size={iconSize}
      color={color}
      className={className}
      viewBox="0 0 20 20"
      {...props}
    >
      <g transform="translate(4.584 4.648)">
        <path d="M5.83333 1.32408C5.83333 1.51329 5.67908 1.78562 5.20242 2.03529C4.74546 2.27467 4.08533 2.43521 3.33333 2.43521C2.58137 2.43521 1.92121 2.27467 1.46429 2.03529C0.987583 1.78562 0.833333 1.51329 0.833333 1.32408C0.833333 1.15883 0.947458 0.9315 1.30654 0.70375L0.860167 0C0.386333 0.300583 0 0.750666 0 1.32408C0 1.97867 0.498667 2.47025 1.07762 2.7735C1.67629 3.08708 2.4745 3.26854 3.33333 3.26854C4.19217 3.26854 4.99037 3.08708 5.58908 2.7735C6.168 2.47025 6.66667 1.97867 6.66667 1.32408C6.66667 0.750666 6.28037 0.300583 5.8065 0L5.36017 0.70375C5.71925 0.9315 5.83333 1.15883 5.83333 1.32408Z" />
      </g>
      <g transform="translate(2.294 2.500)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M0.205839 3.95833C0.205839 5.19829 0.986006 6.30504 2.20721 7.03083C0.342889 8.13979 -0.50604 10.1628 0.31071 11.5775C1.1363 13.0075 3.35121 13.2743 5.25783 12.1735C7.10521 11.1069 7.98533 9.14771 7.28725 7.72621C9.46429 7.21292 11.0392 5.72004 11.0392 3.95833C11.0392 1.77221 8.61404 0 5.6225 0C2.63096 0 0.205839 1.77221 0.205839 3.95833ZM8.961 6.08446C9.78458 5.48258 10.2058 4.72087 10.2058 3.95833C10.2058 3.19579 9.78458 2.43408 8.961 1.83221C8.138 1.23078 6.96037 0.833333 5.6225 0.833333C4.28462 0.833333 3.107 1.23078 2.28404 1.83221C1.46041 2.43408 1.03917 3.19579 1.03917 3.95833C1.03917 4.72087 1.46041 5.48258 2.28404 6.08446C3.107 6.68587 4.28462 7.08333 5.6225 7.08333C6.96037 7.08333 8.138 6.68587 8.961 6.08446ZM4.60821 7.84737C4.90558 7.88854 5.21154 7.91196 5.52396 7.916C5.60629 7.99337 5.67908 8.08354 5.73921 8.18767C6.00741 8.65225 5.90983 9.19146 5.66937 9.63304C5.42487 10.082 5.00158 10.5011 4.46958 10.8083C3.93758 11.1154 3.363 11.2725 2.85191 11.2597C2.34925 11.2472 1.83348 11.062 1.56526 10.5975C1.33089 10.1915 1.37659 9.72504 1.55098 9.32396L2.31521 9.65625C2.20725 9.9045 2.22687 10.0767 2.28696 10.1808C2.35508 10.2988 2.53025 10.4181 2.87271 10.4266C3.20679 10.435 3.63158 10.3298 4.05291 10.0866C4.47425 9.84333 4.77766 9.528 4.9375 9.2345C5.10133 8.93367 5.08562 8.72233 5.0175 8.60433C4.95741 8.50025 4.81808 8.39717 4.54908 8.3665L4.60821 7.84737Z"
        />
      </g>
      <g transform="translate(13.452 7.524)">
        <path d="M1.48023 2.79461C2.47815 3.06198 3.44969 2.67202 3.65023 1.92357C3.85077 1.17515 3.20436 0.351649 2.20644 0.0842742C1.20857 -0.183101 0.237024 0.206858 0.0364828 0.955274C-0.164059 1.70373 0.482316 2.52719 1.48023 2.79461Z" />
      </g>
      <g transform="translate(9.478 10.908)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.12504 4.28172C7.67866 5.94763 5.51612 6.81567 3.29487 6.22047C1.07362 5.6253 -0.365172 3.7923 0.0812025 2.12638C0.527578 0.460466 2.69012 -0.407576 4.91137 0.18759C7.13262 0.782799 8.57141 2.6158 8.12504 4.28172ZM5.23424 3.83105C5.58091 3.72884 5.73233 3.5578 5.77145 3.41172C5.80595 3.28297 5.77608 3.09797 5.59774 2.87372L6.24991 2.35497C6.53199 2.70959 6.70112 3.16197 6.57641 3.62738C6.4337 4.16001 5.97033 4.4828 5.46995 4.63038C4.96154 4.78026 4.34287 4.77742 3.72587 4.61209C3.10883 4.44676 2.57162 4.13988 2.20629 3.75588C1.84674 3.37792 1.60683 2.86667 1.74954 2.33405C1.87424 1.86863 2.24691 1.56142 2.66849 1.39534L2.97395 2.17067C2.70737 2.27567 2.58899 2.42097 2.55449 2.54972C2.51533 2.6958 2.56095 2.91967 2.81008 3.18151C3.05337 3.43726 3.44795 3.67492 3.94154 3.80717C4.43512 3.93942 4.89566 3.93088 5.23424 3.83105Z"
        />
      </g>
    </IconBase>
  );
};

export default IconBloodCount;
